import { AnalyticsUserEvent } from '@fruitful/types';
import { router } from 'expo-router';
import { useAtomValue, useSetAtom } from 'jotai';
import * as React from 'react';
import { useState } from 'react';
import {
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    FruitfulCustomIcon,
    RadioInput,
    redactPrivate,
    ScreenContainer,
    Shadows,
    XStack,
    YStack,
} from 'smoothie';

import { getLastFourAccountNumber } from '../../../common/utils/bankingUtils';
import { useViewEvents } from '../../../events/hooks/useViewEvents';
import { track } from '../../../logger/Logger';
import {
    bankAccountDisplayDetails,
    selectedAccountForDirectDepositAtom,
} from '../../state/SpacesAtoms';
import type { BankAccountDisplayDetail } from '../../types/BankAccount';
import type { DirectDepositSwitchAppContext } from './types';

export const SelectAccount = ({
    defaultBankAccountId,
    context = '',
    source = '',
}: {
    defaultBankAccountId?: string;
    context?: DirectDepositSwitchAppContext | '';
    source?: string;
}) => {
    const { trackViewOnce } = useViewEvents();
    trackViewOnce(`${context}/${source}/DirectDeposit/SelectAccount`);

    const bankAccountDetails = useAtomValue(bankAccountDisplayDetails);
    const setSelectedAccount = useSetAtom(selectedAccountForDirectDepositAtom);

    const defaultAccountIndex = bankAccountDetails.findIndex(
        (d) => d.bankAccountId === defaultBankAccountId,
    );

    const [selectedAccountIndex, setSelectedAccountIndex] = useState<number>(
        Math.max(defaultAccountIndex, 0),
    );

    const redirectHref =
        context === 'roadmap' ? '/home/roadmap' : '/home/banking';

    return (
        <ScreenContainer
            title="Select account to deposit to"
            hasBackButton
            onFallbackRoute={redirectHref}
            onBack={() => {
                track(
                    AnalyticsUserEvent.DIRECT_DEPOSIT_SWITCH_SELECT_ACCOUNT_EXIT,
                    {
                        ddAppContext: context,
                        ddSource: source,
                    },
                );
                router.back();
            }}
        >
            <YStack gap={24} justifyContent="space-between">
                <YStack gap={16} width="100%" maxWidth={600} alignSelf="center">
                    {bankAccountDetails.map(
                        (account: BankAccountDisplayDetail, index) => {
                            return (
                                <XStack
                                    key={account.bankAccountId}
                                    padding={12}
                                    borderWidth={1}
                                    borderRadius={16}
                                    borderColor={
                                        index === selectedAccountIndex
                                            ? '$gray900'
                                            : '$gray400'
                                    }
                                    gap={12}
                                    marginVertical={8}
                                    alignItems="center"
                                    style={{ ...Shadows.SMALL }}
                                    onPress={(e) => {
                                        e?.stopPropagation();
                                        setSelectedAccountIndex(index);
                                    }}
                                    hoverStyle={{
                                        cursor: 'pointer',
                                        backgroundColor: '$gray100',
                                    }}
                                >
                                    <FruitfulCustomIcon
                                        icon={account.icon}
                                        color={account.color}
                                    />
                                    <YStack gap={4} {...redactPrivate()}>
                                        <BodyMediumRegular flexGrow={1}>
                                            {account.nickname}
                                        </BodyMediumRegular>
                                        <BodyMediumRegular flexGrow={1}>
                                            Fruitful *
                                            {getLastFourAccountNumber(
                                                account.accountNumberMasked,
                                            )}
                                        </BodyMediumRegular>
                                    </YStack>
                                    <RadioInput
                                        key={`${account.bankAccountId}.${index}`}
                                        isSelected={
                                            index === selectedAccountIndex
                                        }
                                        tone="neutral"
                                        disabled={false}
                                    />
                                </XStack>
                            );
                        },
                    )}
                </YStack>

                <Button
                    text="Switch now"
                    variant={ButtonVariant.PRIMARY}
                    size={ButtonSize.LARGE}
                    onPress={() => {
                        const selectedAccount =
                            bankAccountDetails[selectedAccountIndex];
                        if (selectedAccount != null) {
                            setSelectedAccount(selectedAccount);
                        }
                        track(
                            AnalyticsUserEvent.DIRECT_DEPOSIT_SWITCH_ACCOUNT_SELECTED,
                            {
                                ddAppContext: context,
                                ddSource: source,
                            },
                        );
                        router.replace({
                            pathname: '/banking/direct-deposit/switch',
                            params: {
                                context,
                                source,
                            },
                        });
                    }}
                />
            </YStack>
        </ScreenContainer>
    );
};
